import { useEffect, useMemo, useRef, useState } from 'react'
import { trpcClient } from '../../../../../utils/trpc'
import { useReportBuilderStore } from '../../stores/reportBuilderStore'

interface FormComponent {
	id: string
	label: string | null
	name: string
	formId: string
	formName: string
}

function formatDate(date: Date): string {
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

function formatDateShort(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	const year = date.getFullYear()
	return `${year}-${month}-${day}`
}

export function StepLabels() {
	const {
		currentConfig,
		updateConfig,
		prevStep,
		generateReport,
		generateRawDataReport,
		generateDistributionReport,
		isGenerating,
		forms,
		cycles,
	} = useReportBuilderStore()

	const isRawDataMode = currentConfig.reportType === 'raw-data'
	const isDistributionMode = currentConfig.reportType === 'distribution'

	// Track if we've already set defaults for this step visit
	const hasSetDefaultsRef = useRef(false)

	// Generate default name and description based on current config
	const defaultNameAndDescription = useMemo(() => {
		const now = new Date()
		const dateShort = formatDateShort(now)
		const dateFriendly = formatDate(now)

		// Get form and cycle names
		const selectedFormNames = forms.filter((f) => currentConfig.selectedFormIds.includes(f.id)).map((f) => f.name)
		const selectedCycleNames = cycles.filter((c) => currentConfig.selectedCycleIds.includes(c.id)).map((c) => c.name)

		// Build report type label
		const reportTypeLabels: Record<string, string> = {
			form: 'Form Report',
			cycle: 'Cycle Report',
			'cross-cycle': 'Cross-Cycle Comparison',
			'raw-data': 'Raw Data Export',
			distribution: 'Distribution Analysis',
		}
		const reportTypeLabel = reportTypeLabels[currentConfig.reportType] || 'Report'

		// Short name for the name field
		let shortName = ''
		if (selectedFormNames.length === 1) {
			// Single form - use form name
			const formName =
				selectedFormNames[0].length > 30 ? `${selectedFormNames[0].slice(0, 27)}...` : selectedFormNames[0]
			shortName = `${formName} - ${dateShort}`
		} else if (selectedFormNames.length > 1) {
			shortName = `${selectedFormNames.length} Forms Report - ${dateShort}`
		} else if (selectedCycleNames.length === 1) {
			const cycleName =
				selectedCycleNames[0].length > 30 ? `${selectedCycleNames[0].slice(0, 27)}...` : selectedCycleNames[0]
			shortName = `${cycleName} - ${dateShort}`
		} else if (selectedCycleNames.length > 1) {
			shortName = `${selectedCycleNames.length} Cycles Report - ${dateShort}`
		} else {
			shortName = `${reportTypeLabel} - ${dateShort}`
		}

		// Friendly description
		let description = `${reportTypeLabel} generated on ${dateFriendly}`

		if (selectedFormNames.length > 0) {
			if (selectedFormNames.length === 1) {
				description += ` for "${selectedFormNames[0]}"`
			} else if (selectedFormNames.length <= 3) {
				description += ` for forms: ${selectedFormNames.join(', ')}`
			} else {
				description += ` for ${selectedFormNames.length} forms`
			}
		}

		if (selectedCycleNames.length > 0) {
			if (selectedCycleNames.length === 1) {
				description += ` (${selectedCycleNames[0]})`
			} else if (selectedCycleNames.length <= 3) {
				description += ` covering cycles: ${selectedCycleNames.join(', ')}`
			} else {
				description += ` covering ${selectedCycleNames.length} cycles`
			}
		}

		if (isDistributionMode) {
			description += '. Shows response distribution across values.'
		} else if (isRawDataMode) {
			description += '. Contains individual response data.'
		} else {
			description += '.'
		}

		return { name: shortName, description }
	}, [
		currentConfig.selectedFormIds,
		currentConfig.selectedCycleIds,
		currentConfig.reportType,
		forms,
		cycles,
		isDistributionMode,
		isRawDataMode,
	])

	// Set defaults when step loads and name is empty (brand new report)
	useEffect(() => {
		if (!hasSetDefaultsRef.current && !currentConfig.name.trim() && !currentConfig.id) {
			updateConfig({
				name: defaultNameAndDescription.name,
				description: defaultNameAndDescription.description,
			})
			hasSetDefaultsRef.current = true
		}
	}, [defaultNameAndDescription, currentConfig.name, currentConfig.id, updateConfig])

	const handleGenerate = () => {
		if (isRawDataMode) {
			generateRawDataReport()
		} else if (isDistributionMode) {
			generateDistributionReport()
		} else {
			generateReport()
		}
	}

	const [formComponents, setFormComponents] = useState<FormComponent[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [editingLabel, setEditingLabel] = useState<string | null>(null)
	const [newLabelValue, setNewLabelValue] = useState('')

	useEffect(() => {
		const fetchComponents = async () => {
			if (currentConfig.selectedFormIds.length === 0) {
				setFormComponents([])
				return
			}

			setIsLoading(true)
			try {
				const components = await trpcClient.client.reportBuilder.getFormComponents.query({
					formIds: currentConfig.selectedFormIds,
				})
				setFormComponents(components)
			} catch (error) {
				console.error('Failed to fetch form components:', error)
			} finally {
				setIsLoading(false)
			}
		}

		fetchComponents()
	}, [currentConfig.selectedFormIds])

	const startEditLabel = (componentId: string, currentLabel: string) => {
		setEditingLabel(componentId)
		// V1 Fix: Use originalLabel as the mapping key (matches applyCustomLabels lookup)
		setNewLabelValue(currentConfig.questionLabelMappings[currentLabel] || currentLabel)
	}

	const saveLabel = (_componentId: string, originalLabel: string) => {
		// V1 Fix: Key by originalLabel (not componentId) so applyCustomLabels can find the mapping
		// applyCustomLabels() uses row.competency (the label string) as lookup key
		if (newLabelValue.trim() && newLabelValue.trim() !== originalLabel) {
			updateConfig({
				questionLabelMappings: {
					...currentConfig.questionLabelMappings,
					[originalLabel]: newLabelValue.trim(),
				},
			})
		} else if (newLabelValue.trim() === originalLabel) {
			const { [originalLabel]: _, ...rest } = currentConfig.questionLabelMappings
			updateConfig({ questionLabelMappings: rest })
		}
		setEditingLabel(null)
		setNewLabelValue('')
	}

	const resetLabel = (_componentId: string, originalLabel: string) => {
		// V1 Fix: Key by originalLabel to match the save behavior
		const { [originalLabel]: _removed, ...rest } = currentConfig.questionLabelMappings
		updateConfig({ questionLabelMappings: rest })
	}

	// V2.1: Include all component types that can provide data for reports
	const V21_COMPONENT_TYPES = [
		'rating',
		'select',
		'number',
		'9boxrating',
		'text',
		'textarea',
		'date',
		'checkbox',
		'radio',
		'multiselect',
		'radio-group',
	]
	const reportableComponents = formComponents.filter((c) => V21_COMPONENT_TYPES.includes(c.name))

	return (
		<div className='space-y-6'>
			<div>
				<h2 className='text-lg font-semibold text-neutral-900 mb-2'>Customize Labels</h2>
				<p className='text-sm text-neutral-600'>
					Rename question labels for your report. These custom labels will appear in the exported report.
				</p>
			</div>

			<div>
				<h3 className='text-sm font-medium text-neutral-700 mb-3'>
					Available Questions ({reportableComponents.length})
				</h3>
				{isLoading ? (
					<div className='flex items-center justify-center p-8'>
						<svg className='animate-spin h-6 w-6 text-blue-600' viewBox='0 0 24 24' aria-hidden='true'>
							<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
							<path
								className='opacity-75'
								fill='currentColor'
								d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
							/>
						</svg>
						<span className='ml-2 text-sm text-neutral-600'>Loading questions...</span>
					</div>
				) : reportableComponents.length === 0 ? (
					<p className='text-sm text-neutral-500 p-4 bg-neutral-50 rounded-lg text-center'>
						{currentConfig.selectedFormIds.length === 0
							? 'Please select at least one form in Data Selection to see available questions.'
							: 'No reportable questions found in the selected forms.'}
					</p>
				) : (
					<ul className='space-y-2 max-h-[300px] overflow-y-auto'>
						{reportableComponents.map((component) => {
							const originalLabel = component.label || component.name
							// V1 Fix: Look up by originalLabel (matches applyCustomLabels behavior)
							const customLabel = currentConfig.questionLabelMappings[originalLabel]
							const displayLabel = customLabel || originalLabel
							const isCustomized = !!customLabel

							return (
								<li
									key={component.id}
									className='flex items-center justify-between p-3 border border-neutral-200 rounded-md bg-white hover:bg-neutral-50'>
									{editingLabel === component.id ? (
										<div className='flex items-center gap-2 flex-1'>
											<input
												type='text'
												value={newLabelValue}
												onChange={(e) => setNewLabelValue(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') saveLabel(component.id, originalLabel)
													if (e.key === 'Escape') {
														setEditingLabel(null)
														setNewLabelValue('')
													}
												}}
												className='flex-1 px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
											/>
											<button
												type='button'
												onClick={() => saveLabel(component.id, originalLabel)}
												className='px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700'>
												Save
											</button>
											<button
												type='button'
												onClick={() => {
													setEditingLabel(null)
													setNewLabelValue('')
												}}
												className='px-3 py-1 border border-neutral-300 text-neutral-600 rounded text-sm hover:bg-neutral-100'>
												Cancel
											</button>
										</div>
									) : (
										<>
											<div className='flex-1'>
												<div className='flex items-center gap-2'>
													<span className='text-sm font-medium text-neutral-900'>{displayLabel}</span>
													{isCustomized && (
														<span className='text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full'>
															Customized
														</span>
													)}
												</div>
												<div className='text-xs text-neutral-500 mt-0.5'>
													<span className='inline-flex items-center gap-1'>
														<span className='px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-[10px] uppercase font-medium'>
															{component.name}
														</span>
														<span>Form: {component.formName}</span>
														{isCustomized && <span>• Original: {originalLabel}</span>}
													</span>
												</div>
											</div>
											<div className='flex items-center gap-1'>
												<button
													type='button'
													onClick={() => startEditLabel(component.id, originalLabel)}
													className='text-neutral-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50'
													title='Edit label'>
													<svg
														className='h-4 w-4'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														aria-hidden='true'>
														<path
															strokeLinecap='round'
															strokeLinejoin='round'
															strokeWidth={2}
															d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
														/>
													</svg>
												</button>
												{isCustomized && (
													<button
														type='button'
														onClick={() => resetLabel(component.id, originalLabel)}
														className='text-neutral-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50'
														title='Reset to original'>
														<svg
															className='h-4 w-4'
															fill='none'
															viewBox='0 0 24 24'
															stroke='currentColor'
															aria-hidden='true'>
															<path
																strokeLinecap='round'
																strokeLinejoin='round'
																strokeWidth={2}
																d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
															/>
														</svg>
													</button>
												)}
											</div>
										</>
									)}
								</li>
							)
						})}
					</ul>
				)}
			</div>

			<div className='border-t border-neutral-200 pt-6'>
				<h3 className='text-sm font-medium text-neutral-700 mb-3'>Report Name</h3>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label htmlFor='report-name-input' className='block text-xs text-neutral-500 mb-1'>
							Name <span className='text-red-500'>*</span>
						</label>
						<input
							id='report-name-input'
							type='text'
							value={currentConfig.name}
							onChange={(e) => updateConfig({ name: e.target.value })}
							placeholder='Enter report name...'
							aria-required='true'
							aria-invalid={!currentConfig.name.trim() || undefined}
							aria-describedby={!currentConfig.name.trim() ? 'report-name-error' : undefined}
							className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
								!currentConfig.name.trim() ? 'border-red-300' : 'border-neutral-300'
							}`}
						/>
						{!currentConfig.name.trim() && (
							<span id='report-name-error' className='text-xs text-red-600 mt-1'>
								Report name is required
							</span>
						)}
					</div>
					<div>
						<label htmlFor='report-description-input' className='block text-xs text-neutral-500 mb-1'>
							Description (optional)
						</label>
						<input
							id='report-description-input'
							type='text'
							value={currentConfig.description || ''}
							onChange={(e) => updateConfig({ description: e.target.value })}
							placeholder='Enter description...'
							className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
						/>
					</div>
				</div>
			</div>

			<div className='flex justify-between'>
				<button
					type='button'
					onClick={prevStep}
					className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
					Back
				</button>
				<button
					type='button'
					onClick={handleGenerate}
					disabled={isGenerating || !currentConfig.name.trim()}
					className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
					{isGenerating ? (
						<span className='flex items-center gap-2'>
							<svg className='animate-spin h-4 w-4' viewBox='0 0 24 24' aria-hidden='true'>
								<circle
									className='opacity-25'
									cx='12'
									cy='12'
									r='10'
									stroke='currentColor'
									strokeWidth='4'
									fill='none'
								/>
								<path
									className='opacity-75'
									fill='currentColor'
									d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
								/>
							</svg>
							Generating...
						</span>
					) : isRawDataMode ? (
						'Generate Raw Data Export'
					) : (
						'Generate & Preview'
					)}
				</button>
			</div>
		</div>
	)
}
